import axios, { type AxiosResponse } from "axios";
import { type TagsToContent } from "interfaces";
import { type BatchResponse } from "interfaces/batchResponse";
import { type DistributedOperationResult } from "types";

import { type Publisher, type Scorm, type ScormFileRequest, type ScormPayload, type TagsOptions } from "./types";
import type { SortingDirection } from "../../../../enums";
import { type Filters, serializeArrayAndFilterNullable } from "../../../../utils/queryUtils";
import { type ScormGroup, type ScormUser } from "../types/models";

const scormApiUrl = "/api/assets/scorms";

export const dataService = {
  deleteItem(ids: number[]): Promise<AxiosResponse<DistributedOperationResult>> {
    return axios.delete(scormApiUrl, { data: { ids } });
  },
  changeVisibility(ids: number[], visibility: boolean): Promise<AxiosResponse<DistributedOperationResult>> {
    return axios.post(`${scormApiUrl}/visibility`, {
      ids,
      visibility,
    });
  },
  getTagsFilterOptions(showPurchased: boolean): Promise<AxiosResponse<TagsOptions>> {
    return axios.get(`${scormApiUrl}/tags`, { params: { showPurchased } });
  },
  getPublishersFilterOptions(): Promise<AxiosResponse<Publisher[]>> {
    return axios.get(`${scormApiUrl}/publishers`);
  },
  addTags(data: TagsToContent): Promise<AxiosResponse<BatchResponse>> {
    return axios.post(`${scormApiUrl}/tags`, data);
  },
  saveTags(id: number, payload: TagsToContent): Promise<AxiosResponse> {
    return axios.put(`${scormApiUrl}/${id}/tags`, payload);
  },
  updateData(id: number, payload: ScormPayload): Promise<AxiosResponse> {
    return axios.put(`${scormApiUrl}/${id}`, payload);
  },
  getSCORM(id: number): Promise<AxiosResponse<Scorm>> {
    return axios.get(`${scormApiUrl}/${id}`);
  },
  generateSas(id: number) {
    return axios.post(`${scormApiUrl}/${id}/package-sas`);
  },
  allocateResources() {
    return axios.post(`${scormApiUrl}/allocate`);
  },
  updateFile(request: ScormFileRequest) {
    return axios.put(`${scormApiUrl}/${request.id}/package`, request);
  },
  getUsers(
    entityId: number,
    skip: number,
    top: number,
    sortBy: string,
    sortOrder: SortingDirection,
    filters: Filters,
    term?: string,
  ): Promise<AxiosResponse<ScormUser[]>> {
    return axios.get(`/api/v4/content/scorm/${entityId}/users`, {
      params: {
        skip,
        top,
        sortBy,
        sortOrder,
        ...filters,
        term,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getGroups(
    entityId: number,
    skip: number,
    top: number,
    filters: Filters,
    term?: string,
  ): Promise<AxiosResponse<ScormGroup[]>> {
    return axios.get(`/api/v4/content/scorm/${entityId}/groups`, {
      params: {
        skip,
        top,
        ...filters,
        term,
      },
      paramsSerializer: serializeArrayAndFilterNullable,
    });
  },
  getPublishedScormsByIds(ids: string[], showPurchased: boolean) {
    const config = {
      params: { showPurchased, ids },
      paramsSerializer: serializeArrayAndFilterNullable,
    };

    return axios.get(`${scormApiUrl}/published`, config);
  },

  updateThumbnail(id: number, fileKey: string) {
    return axios.put(`${scormApiUrl}/${id}/thumbnail`, { fileKey });
  },
};
